import React, { CSSProperties, useEffect } from 'react';
import styled from 'styled-components';
import InputLabel from '@material-ui/core/InputLabel';
import FormLabel from '@material-ui/core/FormLabel';
import InputBase from '@material-ui/core/InputBase';
import FormControl from '@material-ui/core/FormControl';
import { useField } from 'formik';
import { ComponentPropBase } from '../../types';
import { Button } from '../../atoms/Button/Button';
import { useSmartFormContext } from '../../forms/utils/useSmartFormContext';
import { InfoText } from '../../atoms/InfoText/InfoText';
import { HelpText } from '../../atoms';

const StyledHelpText = styled.div`
  margin-top: 8px;
  font-size: 14px;
`;

interface ConditionalProps extends ComponentPropBase {
  fullWidth?: boolean;
  style?: CSSProperties;
  label?: string;
  helpText?: string;
  errors?: string;
  touched?: boolean;
  disabled?: boolean;
  initialValue?: boolean;
  onChange?: (value: boolean) => void;
  buttonsText?: {
    yes: string;
    no: string;
  };
}

export const ConditionalField: React.FC<ConditionalProps> = ({
  label,
  name,
  errors,
  helpText,
  infoText,
  touched,
  type,
  disabled,
  required,
  fullWidth = false,
  initialValue,
  onChange,
  buttonsText,
  ...rest
}) => {
  const [input, , helpers] = useField(name || 'INVALID');

  const { getDisabled, readonly, setFieldValue, submitCount } = useSmartFormContext();
  if (getDisabled(name) || readonly) {
    disabled = true;
  }

  const handleChange = (value: boolean) => {
    onChange ? onChange(!!initialValue) : setFieldValue(name, value);
  };
  // biome-ignore lint/correctness/useExhaustiveDependencies: exception
  useEffect(() => {
    if (typeof initialValue === 'boolean') {
      helpers.setValue(initialValue);
    } else if (initialValue === 'true' || initialValue === 'false') {
      helpers.setValue(initialValue === 'true');
    }
  }, []);

  if (readonly) {
    return (
      <FormControl disabled data-test={`${name}-field-control`} id={name}>
        <FormLabel>{label}</FormLabel>
        <InputBase value={input.value ? buttonsText?.yes || 'Yes' : buttonsText?.no || 'No'} />
      </FormControl>
    );
  }

  return (
    <FormControl
      error={!!errors && !!touched}
      required={required}
      fullWidth={fullWidth}
      data-test={`${name}-field-control`}
      className="field-control"
      {...rest}
    >
      <InputLabel style={{ top: 0, position: 'relative' }} data-test={`${name}-field-label`} shrink htmlFor={name}>
        <InfoText label={label} required={required} tooltipText={infoText} />
      </InputLabel>
      <StyledHelpText style={{ marginTop: 2 }} data-test={`${name}-field-helper`} id={`${name}-helper-text`}>
        {helpText}
      </StyledHelpText>
      <div style={{ display: 'flex', marginTop: 14 }} id={name}>
        <Button
          style={{ marginRight: 16, minWidth: 150 }}
          color={input.value ? 'primary' : 'default'}
          data-test={`${name}-yes-btn`}
          type="button"
          variant="outlined"
          fullWidth={false}
          disabled={disabled}
          onClick={() => handleChange(true)}
        >
          {buttonsText?.yes || 'Yes'}
        </Button>
        <Button
          style={{ minWidth: 150 }}
          color={input.value === false ? 'primary' : 'default'}
          variant="outlined"
          data-test={`${name}-no-btn`}
          type="button"
          fullWidth={false}
          disabled={disabled}
          onClick={() => handleChange(false)}
        >
          {buttonsText?.no || 'No'}
        </Button>
      </div>
      {!!errors && submitCount > 0 && <HelpText dataTest={`${name}-field-error`} error={errors} />}
    </FormControl>
  );
};
